import React, { FC, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import StringUtils from '../../../utils/stringUtils';
import LoadRateAndTotalRates from '../../TwLoadDetail/components/LoadRates/LoadRateAndTotalRates';
import TwInitialsIndicator from '../../TwInitialsIndicator/TwInitialsIndicator';
import NumberUtils from '../../../utils/numberUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import Stack from '@mui/material/Stack';
import FTLTripsIds from '../../TwTripsNumber/TwTripsNumbers';
import { ORDER_STATUS, ORDER_INVOICE_STATUS, Order } from '../../../redux/api/orders/orders.model';
import { Trip } from '../../../redux/api/trips/trips.model';
import FactoringService from '../../../providers/Factoring/Factoring.service';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateTripId } from '../../../redux/features/trip/tripSlice';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import TwLoadTags from '../../TwLoadTag/TwLoadTag';
import { EntityType } from '../../../redux/Global.model';
import OrderMargin from '../../TwLoadDetail/components/OrderMargin/OrderMargin';

interface OrderOverviewProps {
  loadData: Order | null;
  changeTab?: (_event: React.SyntheticEvent, newValue: string, isClickFromTab: boolean) => void;
  reloadData?: () => void;
}

const OrderOverview: FC<OrderOverviewProps> = ({
  loadData,
  changeTab,
  reloadData
}): JSX.Element => {
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const dispatch = useAppDispatch();
  const { enableFlexibleTagging } = useFeatureFlags();
  const ltlEnabled = userData?.tenant?.ltl_enabled || false;

  const loadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const tripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);

  const dispatcherEnable = getTenantSetting('multiple-dispatchers');
  const isEnabledShowCustomerContact = getTenantSetting('show-customer-contact-on-dispatch');

  const { enableFactoring } = useFeatureFlags();

  const divisionsEnabled =
    (userData?.tenantSettings && userData.tenantSettings['divisions']) || false;
  const brokeredEnabled =
    (userData?.tenantSettings && userData.tenantSettings['brokered-loads']) || false;

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;

  const [invoiceStatus, setInvoiceStatus] = useState<string | null>(null);

  useEffect(() => {
    setInvoiceStatus(null);
    if (!enableFactoring) return;
    if (!loadData?.division_id || !loadData.id) return;

    FactoringService.getOrderStatus(loadData?.division_id, [loadData.id]).then((response) => {
      if (!response) return;

      const order = response[0];

      if (order?.factoring_status === 'factored') {
        setInvoiceStatus('Sent to factoring');
      }

      if (
        order?.factoring_status === 'Error getting invoices' ||
        order?.factoring_status === 'Error uploading to SFTP'
      ) {
        setInvoiceStatus('Factoring error');
      }
    });
  }, [loadData, enableFactoring]);

  const showCustomerContact =
    !isDriverOrCarrier || (isDriverOrCarrier && isEnabledShowCustomerContact);

  const showOrderInvoiceStatus = (): string => {
    let status = '';
    if (loadData) {
      switch (loadData.invoice_status) {
        case ORDER_INVOICE_STATUS.notInvoiced:
          status = 'Not invoiced';
          break;
        case ORDER_INVOICE_STATUS.readyForInvoicing:
          status = 'Ready for invoicing';
          break;
        case ORDER_INVOICE_STATUS.invoiceCreated:
          status = 'Created';
          break;
        case ORDER_INVOICE_STATUS.invoiceSent:
          status = 'Sent';
          break;
        case ORDER_INVOICE_STATUS.paid:
          status = 'Paid';
          break;
        default:
          return '';
      }
    }
    return status;
  };

  const handleTripIdClick = (
    e: React.SyntheticEvent,

    tripId: string
  ): void => {
    if (ltlEnabled) {
      // If order overlay is opened, means trips are on the  remaining tabs, we can change tab to that trip
      if (loadDetailOpen) {
        changeTab && changeTab(e, tripId, false);
        return;
      }

      // If trip overlay is opened, means orders are on the tabs, we can set trip id to open that trip
      if (tripDetailOpen) {
        dispatch(updateTripId(tripId));
        changeTab && changeTab(e, '0', true);
        return;
      }
    } else {
      changeTab && changeTab(e, tripId, false);
    }
  };

  const tripsToShow = isDriverOrCarrier
    ? loadData?.trips?.filter(
        (segment: Trip): boolean =>
          isDriver
            ? segment?.drivers.some((driver) => driver.id === userData?.driver?.id) // filter for driver
            : segment?.carrier?.twuser_id === userData?.id // filter for carrier
      )
    : loadData?.trips; // show all if not carrier or driver

  const getOrderMarginTooltip = useMemo((): string => {
    const msg = 'Revenue - Driver Trip Pay - Sum of other Expenses';

    if (!userData) return msg;
    if (!userData.tenantSettings) return msg;

    const isBokeredLoadsOn = userData.tenantSettings['brokered-loads'];

    if (!isBokeredLoadsOn) return msg;

    return 'Revenue - Driver Trip Pay - Payable amount on brokered loads - Sum of other Expenses';
  }, [userData?.tenantSettings]);

  return (
    <Box component="div" className="load-detail__data-container__overview">
      <Box component="div" className="load-detail__data-container__overview--title">
        Overview
      </Box>

      {enableFlexibleTagging && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Tags
          </Box>
          <TwLoadTags
            entity={EntityType.ORDER}
            loadId={loadData?.id || ''}
            tags={loadData?.tags || []}
            containerSx={{ marginBottom: '1rem' }}
            reloadData={reloadData}
          />
        </>
      )}

      {loadData && loadData.raw_customer && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Customer
          </Box>
          <Box component="div" className="load-detail__data-container__overview__customer">
            <TwInitialsIndicator
              text={
                (loadData && (loadData.raw_customer.initials ?? loadData.raw_customer.name)) || ''
              }
              displayRawText={!loadData?.raw_customer.initials}
            />

            <Box component="div" className="load-detail__data-container__overview__customer--data">
              <Box component="span">{loadData && loadData.raw_customer.name}</Box>
              {showCustomerContact && (
                <Box component="span">{loadData && loadData.raw_customer.billing_email}</Box>
              )}
            </Box>
          </Box>
        </>
      )}

      {loadData && showCustomerContact && loadData.raw_customer?.contact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Customer contact
          </Box>
          <Box component="div" className="load-detail__data-container__overview__customer">
            <TwInitialsIndicator
              text={(loadData && loadData.raw_customer.contact?.name) || ''}
              displayRawText={!loadData?.raw_customer.contact?.name}
            />
            <Box component="div" className="load-detail__data-container__overview__customer--data">
              <Box component="span">{loadData && loadData.raw_customer.contact?.name}</Box>
              <Box component="span">
                {loadData.raw_customer.contact?.phone
                  ? StringUtils.formatPhoneNumber(loadData.raw_customer.contact?.phone.toString())
                  : loadData.raw_customer.contact?.email_address}
              </Box>
            </Box>
          </Box>
        </>
      )}

      {loadData && !!loadData.rate && userData?.role !== USER_ROLE.DRIVER && (
        <LoadRateAndTotalRates
          load_rates={loadData.order_rates ?? []}
          rate={loadData.rate}
          rate_per_mile={loadData.rate_per_mile}
          loadSegments={loadData.trips}
          role={userData?.role}
          rateTable={loadData.rate_table}
        />
      )}

      {loadData && !isDriver && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Booking mode
          </Box>
          <Box component="div" className="load-detail__data-container__overview__status">
            {loadData.booking_type}
          </Box>
        </>
      )}

      {loadData && !isDriver && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Order coverage
          </Box>
          <Box component="div" className="load-detail__data-container__overview__status">
            {StringUtils.toReadableOrderCoverageString(loadData.coverage)}
          </Box>
        </>
      )}

      {loadData &&
        brokeredEnabled &&
        loadData.is_brokered_order &&
        !isDriver &&
        !isCustomerContact && (
          <>
            <Box component="div" className="load-detail__data-container__overview--sub-title">
              Payable amount
            </Box>
            <Box component="div" className="load-detail__data-container__overview__status">
              {loadData.payable_amount ? NumberUtils.getCurrency(loadData.payable_amount) : '-'}
            </Box>
            <Box component="div" className="load-detail__data-container__overview--sub-title">
              Margin
            </Box>
            <Box component="div" className="load-detail__data-container__overview__status">
              {loadData.payable_amount && loadData.rate
                ? NumberUtils.getCurrency(loadData.rate - loadData.payable_amount)
                : '-'}
            </Box>
          </>
        )}

      {divisionsEnabled && loadData && loadData.division && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Division
          </Box>
          <Box component="div" className="load-detail__data-container__overview__customer">
            <TwInitialsIndicator
              text={loadData.division.name || ''}
              displayRawText={!loadData.division.name}
            />
            <Box component="div" className="load-detail__data-container__overview__customer--data">
              <Box component="span">{loadData.division.name}</Box>
              <Box component="span">
                {loadData.division.phone
                  ? StringUtils.formatPhoneNumber(loadData.division.phone.toString())
                  : loadData.division.email_address}
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Order status
      </Box>
      <Box component="div" className="load-detail__data-container__overview__status">
        {StringUtils.getOrderStatusLabel(loadData?.status || ORDER_STATUS.NOT_STARTED)}
      </Box>

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Invoice status
      </Box>
      <Box component="div" className="load-detail__data-container__overview__status">
        {showOrderInvoiceStatus()}
      </Box>

      {invoiceStatus && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Factored status
          </Box>
          <Box component="div" className="load-detail__data-container__overview__status">
            {invoiceStatus}
          </Box>
        </>
      )}

      {loadData &&
        loadData.customer_order_number &&
        StringUtils.toNonEmptyString(loadData.customer_order_number) && (
          <>
            <Box component="div" className="load-detail__data-container__overview--sub-title">
              Customer order #
            </Box>
            <Box component="div" className="load-detail__data-container__overview__client-load">
              {loadData.customer_order_number}
            </Box>
          </>
        )}

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Order #
      </Box>
      <Box component="div" className="load-detail__data-container__overview__tw-load">
        {loadData && loadData.readable_id}
      </Box>

      {loadData && loadData.trips?.length > 0 && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Trip #
          </Box>

          <Stack
            flexDirection={'row'}
            flexWrap={'wrap'}
            whiteSpace={'break-spaces'}
            marginBottom="20px"
          >
            {tripsToShow &&
              tripsToShow.map((trip) => {
                return (
                  <FTLTripsIds
                    key={trip.id}
                    tags={loadData.tags || []}
                    orderReadableId={loadData.readable_id || ''}
                    tripReadableId={trip.readable_id || ''}
                    trip={trip}
                    tripOrder={trip.sort_order}
                    tripsLength={loadData.trips.length}
                    clientName={loadData.raw_customer?.name}
                    onTripClick={(e): void => handleTripIdClick(e, trip.id)}
                  />
                );
              })}
          </Stack>
        </>
      )}

      {loadData &&
        loadData.dispatcher &&
        dispatcherEnable &&
        (loadData.dispatcher?.first_name || loadData.dispatcher?.last_name) !== '' && (
          <>
            <Box component="div" className="load-detail__data-container__overview--sub-title">
              Dispatcher
            </Box>
            <Box component="div" className="load-detail__data-container__overview__client-load">
              {loadData.dispatcher.first_name} {loadData.dispatcher.last_name}
            </Box>
          </>
        )}

      {loadData && loadData.order_margin && (
        <OrderMargin
          orderMargin={loadData.order_margin}
          toolTipMsg={getOrderMarginTooltip}
          isBokeredLoadsOn={brokeredEnabled}
        />
      )}
    </Box>
  );
};

export default OrderOverview;
